import React, { useState, useRef, useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { ThemeProvider } from './components/theme-provider';
import { ThemeToggle } from './components/theme-toggle';
import ComparisonForm from './components/ComparisonForm';
import ComparisonResult from './components/ComparisonResult';
import Disclaimer from './components/Disclaimer';
import { Alert, AlertDescription, AlertTitle } from './components/ui/alert';
import { AlertCircle, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

function App() {
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [videoLoading, setVideoLoading] = useState(false);
  const [error, setError] = useState(null);
  const videoPollIntervalRef = useRef(null);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (videoPollIntervalRef.current) {
        clearInterval(videoPollIntervalRef.current);
      }
    };
  }, []);

  const pollForVideo = async (comparisonId) => {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    
    const checkVideoStatus = async () => {
      try {
        console.log(`[App] Checking video status for comparison: ${comparisonId}`);
        const response = await fetch(`${API_URL}/api/comparison/${comparisonId}/video`);
        const data = await response.json();
        
        console.log(`[App] Video status response:`, data);
        
        if (data.status === 'ready' && data.mashup_video) {
          console.log(`[App] ✅ Video is ready!`, data.mashup_video);
          // Video is ready, update comparison data
          setComparisonData(prev => {
            const updated = {
              ...prev,
              mashup_video: data.mashup_video,
              video_status: 'ready'
            };
            console.log(`[App] Updated comparison data:`, updated);
            return updated;
          });
          setVideoLoading(false);
          
          // Stop polling
          if (videoPollIntervalRef.current) {
            clearInterval(videoPollIntervalRef.current);
            videoPollIntervalRef.current = null;
            console.log(`[App] Stopped polling for video`);
          }
          
          toast.success('Video mashup is ready!', { duration: 3000 });
        } else if (data.status === 'failed') {
          console.log(`[App] ❌ Video generation failed`);
          // Video generation failed
          setComparisonData(prev => ({
            ...prev,
            video_status: 'failed'
          }));
          setVideoLoading(false);
          
          // Stop polling
          if (videoPollIntervalRef.current) {
            clearInterval(videoPollIntervalRef.current);
            videoPollIntervalRef.current = null;
          }
          
          toast.error('Video generation failed', { duration: 4000 });
        } else {
          console.log(`[App] ⏳ Video still processing...`);
        }
        // If status is 'processing', continue polling
      } catch (err) {
        console.error('[App] Error checking video status:', err);
        // Continue polling on error (don't stop)
      }
    };
    
    // Poll every 3 seconds
    videoPollIntervalRef.current = setInterval(checkVideoStatus, 3000);
    // Also check immediately
    checkVideoStatus();
  };

  const handleCompare = async (sport, player1, player2, category = null) => {
    setLoading(true);
    setVideoLoading(false);
    setError(null);
    setComparisonData(null);
    
    // Clear any existing polling
    if (videoPollIntervalRef.current) {
      clearInterval(videoPollIntervalRef.current);
      videoPollIntervalRef.current = null;
    }

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${API_URL}/api/comparison`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sport, player1, player2, category }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle API errors
        const errorMessage = data.error || data.message || 'Failed to generate comparison';
        const suggestion = data.suggestion || '';
        
        // Show toast with error and suggestion
        const toastMessage = suggestion 
          ? `${errorMessage}\n\n${suggestion}`
          : errorMessage;
        
        toast.error(toastMessage, { 
          duration: 6000,
        });
        
        setError(errorMessage);
        return;
      }

      // Success - stats are ready, show them immediately
      toast.success('Player statistics loaded!', { duration: 2000 });
      setComparisonData(data);
      setLoading(false); // Stop main loading, stats are shown
      
      // If video is being processed, start polling
      if (data.video_status === 'processing' && data.comparison_id) {
        setVideoLoading(true);
        toast('Video is being generated in the background...', {
          icon: '⏳',
          duration: 4000,
        });
        pollForVideo(data.comparison_id);
      }
    } catch (err) {
      const errorMessage = err.message || 'An error occurred. Please try again.';
      toast.error(
        `❌ ${errorMessage}\n\nPlease check:\n• Backend server is running\n• Both players are from ${sport}\n• API keys are configured`,
        { duration: 5000 }
      );
      setError(errorMessage);
      console.error('Comparison error:', err);
      setLoading(false);
    }
  };

  return (
    <ThemeProvider defaultTheme="dark">
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'hsl(var(--card))',
            color: 'hsl(var(--card-foreground))',
            border: '1px solid hsl(var(--border))',
            borderRadius: 'var(--radius)',
          },
          success: {
            iconTheme: {
              primary: 'hsl(var(--primary))',
              secondary: 'white',
            },
          },
          error: {
            iconTheme: {
              primary: 'hsl(var(--destructive))',
              secondary: 'white',
            },
          },
        }}
      />
      <div className="min-h-screen bg-background">
        {/* Header with Theme Toggle */}
        <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="container flex h-16 items-center justify-between px-4 md:px-6">
            <div className="flex items-center space-x-2">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">
                🏆 Sports Player Comparison
              </h1>
            </div>
            <ThemeToggle />
          </div>
        </header>

        <main className="container mx-auto px-4 py-8 md:px-6 md:py-12 max-w-7xl">
          {/* Hero Section */}
          <div className="text-center mb-12 space-y-4">
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight">
              AI-Powered Multi-Sport Analysis
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Compare your favorite athletes across multiple sports with AI-driven insights
            </p>
          </div>

          {/* Comparison Form */}
          <div className="mb-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <ComparisonForm onCompare={handleCompare} loading={loading} />
          </div>
          
          {/* Error Message */}
          {error && (
            <Alert variant="destructive" className="mb-8 animate-in fade-in slide-in-from-top-4 duration-500">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>
                {error}
                <span className="block mt-2 text-sm opacity-90">
                  Make sure your backend server is running and API keys are configured.
                </span>
              </AlertDescription>
            </Alert>
          )}

          {/* Loading State - Only shown when stats are loading */}
          {loading && (
            <div className="space-y-8 mb-8 animate-in fade-in duration-500">
              <div className="flex flex-col items-center justify-center space-y-4 p-12 rounded-lg border bg-card">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-lg font-medium">Generating AI-powered comparison...</p>
                <div className="w-full max-w-md space-y-4 mt-6">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Fetching player statistics...</span>
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Generating AI analysis...</span>
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    </div>
                  </div>
                  <div className="mt-4 p-4 rounded-lg bg-muted/50 border border-dashed">
                    <p className="text-xs text-muted-foreground text-center">
                      💡 Statistics will appear first, then video will load in the background
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Comparison Result */}
          {comparisonData && (
            <div className="animate-in fade-in slide-in-from-bottom-8 duration-700">
              <ComparisonResult data={comparisonData} videoLoading={videoLoading} />
            </div>
          )}
          
          {/* Disclaimer */}
          <div className="mt-12 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <Disclaimer />
          </div>
        </main>

        {/* Footer */}
        <footer className="border-t mt-16 py-8">
          <div className="container mx-auto px-4 md:px-6 text-center text-sm text-muted-foreground">
            <p>© 2024 Sports Player Comparison. AI-powered analysis for entertainment purposes.</p>
          </div>
        </footer>
      </div>
    </ThemeProvider>
  );
}

export default App;
